import clsx from 'clsx';
import React from 'react';

import { PrimaryButton } from '@components/Button/variants';
import TIPollsAppIcon from '@components/Images/Rebranding/TIPollsAppIcon';
import LazyImgixImage from '@components/LazyImgixImage';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { getImgixSrc } from '@utils/images';

import { CTA_BY_PLATFORM } from '../DownloadCta';
import { DevicePlatform } from '../constants';
import styles from './styles.module.scss';

const APP_SCREENSHOT_ALT =
  'Polls app asking whether AI agents will replace most SaaS seats by 2030';

const appScreenshotSrc = (path: string) => getImgixSrc({ path, isEnvDependent: false });

const Hero = ({ platform, qrUrl }: { platform: DevicePlatform; qrUrl: string }) => {
  const { caption, href } = CTA_BY_PLATFORM[platform];
  const { trackEvent } = useEventTracking();

  return (
    <section className={clsx(styles.hero, 'overflow-hidden', '-mt-32 nav:-mt-64')}>
      <div aria-hidden="true" className={styles.background} />
      <div className="relative mx-auto flex w-full max-w-[1440px] flex-col px-20 pb-0 pt-28 sm:px-40 sm:pt-40 lg:px-64 lg:pt-48 xl:px-80 xl:pt-56">
        <div className="flex items-center justify-center gap-10 xs:justify-start sm:gap-12">
          <TIPollsAppIcon className="h-40 w-40 shrink-0" aria-hidden="true" />
          <div className="flex flex-col">
            <span className="font-suisse-intl text-[18px] font-bold leading-none text-white sm:text-[20px]">
              Polls
            </span>
            <span className="mt-4 font-suisse-intl text-[12px] leading-none text-[#b7b2b6] sm:text-[13px]">
              by The Information
            </span>
          </div>
        </div>
        <div className="mt-28 flex flex-col min-[688px]:flex-row min-[688px]:items-start min-[688px]:gap-32 sm:mt-32 lg:mt-40 xl:mt-48">
          <div className="mx-auto max-w-[340px] text-center xs:mx-0 xs:max-w-[520px] xs:text-left min-[688px]:max-w-none min-[688px]:flex-1 min-[688px]:pb-40">
            <h1 className="m-0 font-suisse-intl text-[34px] font-bold leading-[1.08] tracking-tight text-white xs:text-[42px] sm:text-[46px] lg:text-[52px] xl:text-[68px]">
              Think you know <br className="xs:hidden min-[688px]:block" />
              tech? <br className="hidden xs:block min-[688px]:hidden" />
              <span className="text-primary-magenta">Prove it.</span>
            </h1>
            <p className="m-0 mx-auto mt-16 max-w-[280px] font-suisse-intl text-[15px] font-normal leading-[1.45] text-[#c8c3c6] xs:mx-0 xs:max-w-[480px] sm:mt-20 sm:max-w-[460px] sm:text-[16px] lg:max-w-[420px] lg:text-[17px] xl:mt-24 xl:max-w-[460px] xl:text-[18px]">
              Five quick polls a day on the biggest stories in tech. Answer, then see what the
              industry really thinks.
            </p>
            {platform !== 'desktop' ? (
              <div className="mt-28 flex flex-col items-center gap-12 xs:mt-32 xs:flex-row xs:items-center xs:justify-start xs:gap-16 lg:mt-36">
                <PrimaryButton
                  onClickCapture={() =>
                    trackEvent(
                      click({
                        clickTarget: ClickTarget.PollsAppDownloadCta,
                        clickText: 'Download app free',
                        clickLink: href,
                      })
                    )
                  }
                  as="externalLink"
                  href={href}
                  size="small"
                  className="!w-fit !rounded-full !px-24 !py-12 !text-[16px] !text-white !no-underline hover:!border-primary-magenta hover:!bg-primary-magenta hover:!text-white focus:!border-primary-magenta focus:!bg-primary-magenta focus:!text-white"
                >
                  Download app free
                </PrimaryButton>
                <p className="m-0 font-suisse-intl text-[13px] leading-none text-[#b7b2b6] sm:text-[14px]">
                  {caption}· Free with a work email
                </p>
              </div>
            ) : (
              qrUrl && (
                <div className="mt-28 flex flex-col items-center gap-12 xs:mt-32 xs:flex-row xs:items-center xs:justify-start xs:gap-16 lg:mt-36">
                  <img
                    src={qrUrl}
                    alt="Polls app QR code"
                    className="block h-[128px] w-[128px] rounded-8 bg-white"
                  />
                  <p className="m-0 font-suisse-intl text-[13px] leading-none text-[#b7b2b6] sm:text-[14px]">
                    {caption}
                  </p>
                </div>
              )
            )}
          </div>
          <div className="pointer-events-none mt-36 flex justify-center xs:mt-40 min-[688px]:mt-0 min-[688px]:w-[46%] min-[688px]:shrink-0 min-[688px]:items-end min-[688px]:self-stretch lg:w-[52%]">
            <LazyImgixImage
              alt={APP_SCREENSHOT_ALT}
              className="block h-auto w-[260px] max-w-full xs:w-[320px] min-[688px]:w-[83%] lg:w-[73%]"
              fit="max"
              sizes="(min-width: 1350px) 490px, (min-width: 688px) 38vw, 320px"
              src={appScreenshotSrc('landing_pages/polls_app/AppPollsV2.png')}
              visibleByDefault
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
